import clsx from "clsx";
import { FC, MouseEventHandler, PropsWithChildren, ReactNode } from "react";
import Button from "../Button";
import Icon, { IconType } from "../Icon";

export type InlineNotificationType =
  | "info"
  | "info-marigold"
  | "warning"
  | "error"
  | "neutral";

export type InlineNotificationSize = "sm" | "lg";

export interface InlineNotificationProps {
  type?: InlineNotificationType;
  onClick?: MouseEventHandler;
  className?: string;
  size?: InlineNotificationSize;
  icon?: IconType | ReactNode;
  closeable?: boolean;
  onClose?: VoidFunction;
}

const colors = {
  info: "bg-info-50",
  "info-marigold": "bg-marigold-50",
  warning: "bg-warning",
  error: "bg-error-50",
  neutral: "bg-neutral-12",
};

const iconColors = {
  info: "text-info-700",
  "info-marigold": "text-marigold-750",
  warning: "",
  error: "text-error-625",
  neutral: "",
};

interface InlineNotificationIconProps {
  icon: string | ReactNode;
  type: InlineNotificationType;
  size: InlineNotificationSize;
}

const InlineNotificationIcon: FC<InlineNotificationIconProps> = ({
  icon,
  type,
  size,
}) => {
  if (!icon && type === "error") {
    return <Icon type="Attention" size={size === "lg" ? 20 : 16} />;
  }
  if (typeof icon === "string") {
    return <Icon type={icon as IconType} size={size === "lg" ? 20 : 16} />;
  }
  return icon;
};

const InlineNotification: FC<PropsWithChildren<InlineNotificationProps>> = ({
  type = "info",
  className,
  size = "sm",
  icon,
  closeable,
  onClose,
  children,
}) => {
  const color = colors[type];
  const iconColor = iconColors[type];

  return (
    <div
      className={clsx("flex items-center gap-3xs", color, className, {
        "rounded-xs px-s py-2xs": size === "sm",
        "rounded-m px-s m:px-m py-3xs": size === "lg",
      })}
    >
      <div className={clsx("flex", iconColor)}>
        <InlineNotificationIcon size={size} type={type} icon={icon} />
      </div>
      <div
        className={clsx("flex-1", {
          "text-app-h3": size === "lg",
          "text-app-body-1": size === "sm",
        })}
      >
        {children}
      </div>
      {closeable && (
        <div className="pr-2xs">
          <Button
            variant="ghost"
            size="sm"
            iconStart="CloseSmall"
            onClick={onClose}
          />
        </div>
      )}
    </div>
  );
};

export default InlineNotification;
